import { CIMSModule } from '@cims-online/shared'

enum LocalStorageKey {
  ProjectId = 'projectId',
  PageId = 'pageId',
  Path = 'path',
  DrawerWidthRatio = 'drawerWidthRatio',
}

const deprecatedKeys = [
  'sharePointSiteUrl',
  'pathname',
  'search',
  'visiblePayItemConnectionGroups',
  'batchReportArgs',
  'createBatchReportRequestInput',
]

/** @see {@link cimsStorage} */
class CIMSStorage {
  /** initializes a new instance of {@link CIMSStorage} */
  constructor() {
    // remove deprecated keys
    deprecatedKeys.forEach((key) => localStorage.removeItem(key))

    // remove/save old local storage values
    const projectIdKey = 'projectGlobalId'
    const projectId = localStorage.getItem(projectIdKey)
    if (projectId) localStorage.setItem(LocalStorageKey.ProjectId, projectId)
    localStorage.removeItem(projectIdKey)

    const navKeyKey = 'navKey'
    const key = localStorage.getItem(navKeyKey)
    if (key) localStorage.setItem(LocalStorageKey.PageId, key)
    localStorage.removeItem(navKeyKey)
  }

  /**
   * pulls the value associated with the key from local storage and normalizes
   * it into an optional string
   *
   * @param key
   */
  private get(key: LocalStorageKey) {
    const value = localStorage.getItem(key) ?? 'undefined'
    if (['undefined', 'null'].includes(value)) return undefined
    return value
  }
  /**
   * sets the value associated with the key in local storage, returning this
   * for chaining
   *
   * @param key
   * @param value
   */
  private set(key: LocalStorageKey, value: string | undefined) {
    localStorage.setItem(key, value ?? 'undefined')
    return this
  }

  /** gets the current path from localStorage */
  get path() {
    const path = this.get(LocalStorageKey.Path)
    return path
  }

  /**
   * __THIS SHOULD ONLY BE CALLED FROM WITHIN GlobalStateContext!__
   *
   * sets a new path in local storage
   */
  setPath(path: string) {
    return this.set(LocalStorageKey.Path, path)
  }

  /**
   * Gets the global drawer width ratio from localStorage.
   *
   * The value is stored as a ratio of the viewport width so the drawer can
   * resize correctly when users move between differently sized screens.
   */
  get drawerWidthRatio() {
    const drawerWidthRatio = this.get(LocalStorageKey.DrawerWidthRatio)
    if (!drawerWidthRatio) return undefined

    const parsed = Number(drawerWidthRatio)
    if (!Number.isFinite(parsed)) return undefined
    return parsed
  }

  /**
   * Sets the global drawer width ratio in localStorage.
   *
   * @param drawerWidthRatio The drawer width as a ratio of the viewport width.
   */
  setDrawerWidthRatio(drawerWidthRatio: number | undefined) {
    return this.set(
      LocalStorageKey.DrawerWidthRatio,
      drawerWidthRatio?.toString()
    )
  }

  /** gets the current project global id from localStorage */
  get projectId() {
    const projectId = this.get(LocalStorageKey.ProjectId)
    return projectId
  }
  /**
   * __THIS SHOULD ONLY BE CALLED FROM WITHIN GlobalStateContext!__
   *
   * sets a new project global id in local storage
   */
  setProjectId(projectId: string | undefined) {
    return this.set(LocalStorageKey.ProjectId, projectId)
  }

  /** gets the current CIMSPageItem id or CIMSModuleItem from local storage */
  get pageId() {
    let pageId = this.get(LocalStorageKey.PageId)
    if (pageId) return pageId
    pageId = CIMSModule.Home.id
    this.setPageId(pageId)
    return pageId
  }
  /**
   * __THIS SHOULD ONLY BE CALLED FROM WITHIN GlobalStateContext!__
   *
   *  sets a new CIMSPageItem or CIMSModuleItem id in local storage
   */
  setPageId(pageId: string | undefined) {
    return this.set(LocalStorageKey.PageId, pageId)
  }
}

/**
 * handles all the {@link localStorage} needs associated with the application
 *
 * You shouldn't ever directly use localStorage, opting to use the getters/setters
 * on this instance instead
 *
 * if you need more localStorage fields, add them to {@link CIMSStorage}
 */
export const cimsStorage = new CIMSStorage()
